import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Book, Users, FileText, Plus, Bookmark, Clock, TrendingUp, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import SearchBar from '../../components/SearchBar';

/**
 * School Home Page component
 * Modern design with school colors and fixed statistics
 */
const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    courseCount: 0,
    professorCount: 0,
    materialCount: 0,
  });
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  
  // Extract school colors from user data
  const primaryColor = user?.school?.primaryColor || '#FF4500'; // Default to EDX Red color
  const secondaryColor = user?.school?.secondaryColor || '#FFFFFF';
  
  // Create CSS variables for the school colors
  useEffect(() => {
    if (user?.school) {
      document.documentElement.style.setProperty('--school-primary', primaryColor);
      document.documentElement.style.setProperty('--school-secondary', secondaryColor);
      
      // Create lighter and darker variants
      document.documentElement.style.setProperty('--school-primary-light', adjustColor(primaryColor, 40));
      document.documentElement.style.setProperty('--school-primary-dark', adjustColor(primaryColor, -40));
    }
  }, [user, primaryColor, secondaryColor]);
  
  // Helper function to lighten or darken a color
  const adjustColor = (color, amount) => {
    const clamp = (val) => Math.min(255, Math.max(0, val));
    
    if (color.startsWith('#')) {
      let r = parseInt(color.slice(1, 3), 16);
      let g = parseInt(color.slice(3, 5), 16);
      let b = parseInt(color.slice(5, 7), 16);
      
      r = clamp(r + amount);
      g = clamp(g + amount);
      b = clamp(b + amount);
      
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    return color;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch stats, recent materials from subscribed courses, and subscriptions
        const [statsRes, materialsRes, subscriptionsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/schools/${user.school.id}/stats`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          // Changed endpoint to get materials only from subscribed courses
          fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/subscribed-materials/recent`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/subscriptions`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);
        
        // Process responses
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          // Extract the stats we need for the cards
          setStats({
            courseCount: statsData.academic?.courses || 0,
            professorCount: statsData.professors?.total || 0,
            materialCount: statsData.materials?.total || 0,
          });
        } else {
          console.error('Failed to fetch stats:', await statsRes.text());
          toast.error('Could not load statistics');
        }
        
        if (materialsRes.ok) {
          const materialsData = await materialsRes.json();
          setRecentMaterials(materialsData);
        } else {
          console.error('Failed to fetch materials:', await materialsRes.text());
        }
        
        if (subscriptionsRes.ok) {
          const subscriptionsData = await subscriptionsRes.json();
          setSubscriptions(subscriptionsData);
        } else {
          console.error('Failed to fetch subscriptions:', await subscriptionsRes.text());
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.school?.id) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);
  
  // Generate stat card styles based on school colors
  const getCardStyle = (index) => {
    const colors = [
      { 
        bg: 'bg-opacity-10 dark:bg-opacity-20',
        icon: 'text-opacity-90 dark:text-opacity-90',
        gradient: 'from-opacity-10 to-opacity-5 dark:from-opacity-20 dark:to-opacity-10'
      },
      { 
        bg: 'bg-opacity-15 dark:bg-opacity-25',
        icon: 'text-opacity-90 dark:text-opacity-90',
        gradient: 'from-opacity-15 to-opacity-5 dark:from-opacity-25 dark:to-opacity-15'
      },
      { 
        bg: 'bg-opacity-20 dark:bg-opacity-30',
        icon: 'text-opacity-90 dark:text-opacity-90',
        gradient: 'from-opacity-20 to-opacity-10 dark:from-opacity-30 dark:to-opacity-20'
      }
    ];
    
    return colors[index % colors.length];
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header with school branding - Mobile Optimized */}
      <div className="mb-8 relative overflow-hidden rounded-xl p-4 sm:p-6" 
          style={{
            background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}44 100%)`,
            borderBottom: `3px solid ${primaryColor}`
          }}>
        <div className="absolute top-0 left-0 w-full h-full opacity-10"
            style={{
              backgroundImage: `radial-gradient(circle at 25px 25px, ${secondaryColor} 2%, transparent 0%), 
                                radial-gradient(circle at 75px 75px, ${secondaryColor} 2%, transparent 0%)`,
              backgroundSize: '100px 100px'
            }}></div>
        
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white flex flex-col sm:flex-row sm:items-center sm:gap-3">
            <span style={{ color: primaryColor }}>Welcome to</span> 
            <span className="mt-1 sm:mt-0 break-words">{user?.school?.name || 'Your School'}</span>
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-2xl">
            Access courses, materials, and questions. Enhance your academic journey with EDX Red's collaborative platform.
          </p>
        </div>
      </div>
      
      {/* Search box */}
      <div className="mb-8 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
          <span className="inline-block mr-2"
                style={{ color: primaryColor }}>
            Quick Search
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400 font-normal ml-auto">
            Find courses, materials, and professors
          </span>
        </h2>
        <SearchBar />
      </div>
      
      {/* Merit Badge - New section */}
      {user?.merit > 0 && (
        <div className="mb-8 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg border-l-4 overflow-hidden"
             style={{ borderLeftColor: primaryColor }}>
          <div className="flex items-center">
            <div className="p-3 rounded-full mr-4"
                 style={{ 
                   color: primaryColor,
                   backgroundColor: `${primaryColor}22` 
                 }}>
              <Award size={28} />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                Your Merit Score
              </p>
              <div className="flex items-center gap-3">
                <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                  {user?.merit || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Keep contributing to earn more merit!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent materials from subscribed courses */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Recent Materials from My Courses
            </h2>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
              <Clock size={14} className="mr-1" />
              Latest uploads
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-md mr-3"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentMaterials.length > 0 ? (
            <div className="space-y-4">
              {recentMaterials.map((material) => (
                <div 
                  key={material.id} 
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-300 hover:shadow-md hover:border-opacity-0"
                  style={{ borderLeft: `4px solid ${primaryColor}` }}
                >
                  <div className="flex items-start">
                    <div 
                      className="p-2 rounded-md mr-3"
                      style={{ 
                        color: primaryColor,
                        backgroundColor: `${primaryColor}22` 
                      }}
                    >
                      <FileText size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {material.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {material.courseName} • {material.userName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {new Date(material.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <Link
                      to={`/courses/${material.courseId}/sections/${material.sectionId}`}
                      className="text-sm hover:underline"
                      style={{ color: primaryColor }}
                    >
                      View material
                    </Link>
                  </div>
                </div>
              ))}
              
              <div className="text-center mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                <Link
                  to="/courses"
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full transition-all duration-300"
                  style={{ 
                    color: 'white',
                    backgroundColor: primaryColor,
                    boxShadow: `0 4px 14px ${primaryColor}55`
                  }}
                >
                  <Plus size={16} className="mr-1" />
                  Browse more materials
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
              <FileText size={40} className="mx-auto mb-2 opacity-40" />
              <p>No recent materials in your subscribed courses</p>
              <p className="text-sm mt-2">
                Subscribe to more courses or check back later for updates
              </p>
              <Link
                to="/courses"
                className="inline-flex items-center mt-4 text-sm px-4 py-2 rounded-full transition-all duration-300"
                style={{ 
                  color: 'white',
                  backgroundColor: primaryColor
                }}
              >
                <Plus size={16} className="mr-1" />
                Browse courses
              </Link>
            </div>
          )}
        </div>
        
        {/* My subscriptions */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              My Courses
            </h2>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
              <Bookmark size={14} className="mr-1" />
              Subscribed courses
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-md mr-3"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : subscriptions.length > 0 ? (
            <div className="space-y-4">
              {subscriptions.map((subscription) => (
                <div 
                  key={subscription.id} 
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-300 hover:shadow-md hover:border-opacity-0"
                  style={{ borderLeft: `4px solid ${primaryColor}` }}
                >
                  <div className="flex items-start">
                    <div 
                      className="p-2 rounded-md mr-3"
                      style={{ 
                        color: primaryColor,
                        backgroundColor: `${primaryColor}22` 
                      }}
                    >
                      <Book size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {subscription.courseCode}: {subscription.courseName}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {subscription.professorName} • {subscription.semester} {subscription.year}
                      </p>
                      <div className="flex items-center mt-1">
                        <span className="text-xs px-2 py-0.5 rounded"
                              style={{ 
                                color: primaryColor,
                                backgroundColor: `${primaryColor}22`
                              }}>
                          {subscription.departmentCode}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <Link
                      to={`/courses/${subscription.courseId}/sections/${subscription.sectionId}`}
                      className="text-sm hover:underline"
                      style={{ color: primaryColor }}
                    >
                      View course
                    </Link>
                  </div>
                </div>
              ))}
              
              <div className="text-center mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                <Link
                  to="/my-courses"
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full transition-all duration-300"
                  style={{ 
                    color: 'white',
                    backgroundColor: primaryColor,
                    boxShadow: `0 4px 14px ${primaryColor}55`
                  }}
                >
                  View all my courses
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
              <Book size={40} className="mx-auto mb-2 opacity-40" />
              <p>You haven't subscribed to any courses yet</p>
              <Link
                to="/courses"
                className="inline-flex items-center mt-4 text-sm px-4 py-2 rounded-full transition-all duration-300"
                style={{ 
                  color: 'white',
                  backgroundColor: primaryColor
                }}
              >
                <Plus size={16} className="mr-1" />
                Browse courses
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;